import { useEffect, useState } from "react";
import UseCaseCard from "./components/UseCaseCard";
//@ts-ignore
import { MouseIcon, Phone } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import RetellCallDashboard from "./RetellDialog";
import VoicexCallDashboard from "../../temp-demo-components/THABL/VoicexCallInterface";
//@ts-ignore
import { newAgentCategories } from "../../utils/newPageBuilder";
//@ts-ignore;
import { HeartPulse } from "lucide-react";
import CFALogo from "@/assets/Cfa.png";
import CfaAgent from "@/assets/cfaAgent.png";
import hoablGoldLogo from "@/assets/hoabl_gold_logo-03_720.png";
import AgentImage from "@/assets/hoabl_agent_image.png";
import MainPageImage from "@/assets/Screenshot 2025-07-24 at 6.18.32 PM.png";

interface RenderPageProps {
  page_id?: string;
  page_title: string;
  page_path: string;
  page_description?: string;
  page_category: string;
}

interface VoiceOption {
  id: string;
  name: string;
  icon: string;
  description?: string;
  language?: string;
  region?: string;
}

interface FetchedAgent {
  agent_id: string;
  agent_name: string;
  agent_role: string;
  icon: string;
  chat_icon: string;
  feature_list: any[];

  voice: boolean;
  chat: boolean;

  chat_hints: [];
  call_hints?: any[];
  call_hints_table: any[];
  voice_inputs: string[];

  voice_provider: string;
  voice_config: any;
  description?: string;
  call_title?: string;
  sub_description?: string;
  default_voice_id?: string;
  optional_voices?: any[];
  try_speaking?: string[];
  activities?: string[];
  region?: string;
  language?: string;
}

interface Agent {
  id: string;
  callHints: any[];
  callHintsTable: any[];
  name: string;
  description: string;
  subDescription?: string;
  language: string;
  icon: string;
  phoneNumber: string;
  agentName: string;
  chatIcon: string;
  featureList: string[];
  voice: string;
  chat: string;
  chatHints?: any[];
  callTitle?: string;
  voiceInputs: any[];
  voiceProvider: string;
  voiceConfig: any;
  default_voiceId?: string;
  optional_voices: any[];
  try_speaking?: string[];
  activities?: string[];
}

interface SelectedConfig {
  agent: Agent;
  voiceProvider: string;
  selectedVoice: VoiceOption;
}

export default function CFAPage({
  page_id,
  page_path,
  page_title,
  page_description,
  page_category
}: RenderPageProps) {
  const [selectedUseCase, setSelectedUseCase] = useState(0);
  const [selectedVoiceIndex, setSelectedVoiceIndex] = useState(0);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);

  const [selectedConfig, setSelectedConfig] = useState<SelectedConfig | null>(
    null
  );

  const fetchAgents = async () => {
    try {
      setLoading(true);

      const category = Object.values(newAgentCategories).find(
        (cat: any) => cat.path === page_path
      );

      if (category) {
        //@ts-ignore
        setAgents(category.agents);
      } else {
        setAgents([]);
      }
    } catch (error) {
      setError("Failed to fetch agents");
    } finally {
      setLoading(false);
    }
  };

  const getDefaultVoice = (agent: Agent): VoiceOption => {
    return (
      agent.optional_voices.find(
        (voice) => voice.id === agent.default_voiceId
      ) || agent.optional_voices[0]
    );
  };

  const getSelectedVoice = (): VoiceOption => {
    if (agents.length > 0 && selectedUseCase < agents.length) {
      const currentAgent = agents[selectedUseCase];
      return (
        currentAgent.optional_voices[selectedVoiceIndex] ||
        getDefaultVoice(currentAgent)
      );
    }
    return { id: "", name: "", icon: "" };
  };

  useEffect(() => {
    if (agents.length > 0 && selectedUseCase < agents.length) {
      const currentAgent = agents[selectedUseCase];
      const currentVoice = getSelectedVoice();

      setSelectedConfig({
        agent: currentAgent,
        voiceProvider: currentAgent.voiceProvider,
        selectedVoice: currentVoice
      });
    }
  }, [selectedUseCase, selectedVoiceIndex, agents]);

  useEffect(() => {
    setSelectedVoiceIndex(0);
  }, [selectedUseCase]);

  const toggleDialog = () => {
    setIsDialogOpen(!isDialogOpen);
  };

  const handleEndCall = () => {
    setIsCallActive(false);
    setIsDialogOpen(false);
  };

  const handleUseCaseSelect = (index: number) => {
    setSelectedUseCase(index);
  };

  const handleVoiceSelect = (index: number) => {
    setSelectedVoiceIndex(index);
  };

  const renderVoiceDashboard = () => {
    if (!selectedConfig) return null;

    const dashboardProps = {
      pageCategory: page_category,
      isDialogOpen,
      toggleDialog,
      agentName: selectedConfig.selectedVoice.name,
      callHints: selectedConfig.agent.callHints,
      callHintsTable: selectedConfig.agent.callHintsTable,
      callTitle: selectedConfig.agent.callTitle || "",
      icon: selectedConfig.selectedVoice.icon,
      phoneNumber: selectedConfig.agent.phoneNumber,
      features: selectedConfig.agent.featureList,
      try_speaking: selectedConfig.agent.try_speaking || [],
      activities: selectedConfig.agent.activities || []
    };

    switch (selectedConfig.voiceProvider) {
      case "retell":
        return (
          <RetellCallDashboard
            {...dashboardProps}
            onEndCall={handleEndCall}
            agentId={selectedConfig.agent.id}
          />
        );
      case "voicex":
        return (
          <VoicexCallDashboard
            {...dashboardProps}
            voiceConfig={{
              ...selectedConfig.agent.voiceConfig,
              selectedVoiceId: selectedConfig.selectedVoice.id
            }}
            dynamicVariables={{}}
            ttsConfigId={""}
          />
        );
      default:
        return (
          <div className="flex items-center justify-center h-full w-full bg-gray-100">
            <div className="text-center">
              <h2 className="font-inter text-2xl font-semibold mb-4">
                Unsupported Voice Provider
              </h2>
              <p className="font-manrope text-gray-600 mb-4">
                Voice provider "{selectedConfig.voiceProvider}" is not
                supported.
              </p>
              <button
                onClick={toggleDialog}
                className="font-manrope mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Close
              </button>
            </div>
          </div>
        );
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <span className="font-manrope">Loading...</span>
      </div>
    );
  }

  return (
    <div
      className="fixed top-0 left-0 w-screen min-h-screen"
      style={{
        background: `linear-gradient(180deg, #D61920 0%, #D61920 38%, #FFFFFF 38%, #FFFFFF 100%)`,
        zIndex: 0,
      }}
    >
      <div
        className={`flex items-center justify-center`}
        style={{ minHeight: "calc(100vh - 64px)" }}
      >
        <div className="absolute bottom-0 ml-36 left-0 mix-blend-multiply">
                  <img 
                    src={MainPageImage} 
                    alt="Main Page" 
                    className="w-[300px] md:w-[550px] h-auto object-contain select-none"
                    style={{
                      filter: 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.5))',
                      mixBlendMode: 'multiply'
                    }}
                  />
                </div>
        <div
          className="w-full max-w-none md:w-[1000px] bg-gray-50"
          style={{
            minHeight: "calc(100vh - 64px)",
            background: "transparent",
          }}
        >
          <div className="min-h-full">
            <div className="flex flex-col md:flex-row h-full md:space-x-8 md:justify-center">
              {/* Left Panel - Form Section */}
              <div className="w-full md:flex-1 p-4 md:p-12 bg-transparent flex flex-col h-full overflow-y-auto">
                
              <img
                src={hoablGoldLogo}
                alt="Hoabl Gold Logo"
                className="mb-8 md:mb-12"
                style={{ filter: "drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))" }}
              />
                

                {/* <div className="w-full h-px bg-gray-200 mb-6 md:mb-8"></div> */}
                
                <div className="hidden md:mt-8 2xl:mt-20 mb-8 md:mb-12 text-left flex-shrink-0">
                  <h2 className="font-inter text-base md:text-lg  text-gray-500 mb-3 md:mb-4">
                    Select Usecase
                  </h2>
                  <div className="relative">
                    <div className="space-y-3 max-h-[320px] overflow-y-auto">
                      {agents &&
                        agents.map((item, index) => (
                          <UseCaseCard
                            key={index}
                            title={item.name}
                            description={item.description}
                            isSelected={selectedUseCase === index}
                            isFirst={index === 0}
                            onClick={() => handleUseCaseSelect(index)}
                          />
                        ))}
                    </div>
                    {agents && agents.length > 4 && (
                      <div className="text-center mt-12 flex flex-row justify-center gap-1">
                        <MouseIcon
                          size={14}
                          className="text-gray-400 mt-0.5"
                          strokeWidth={1.8}
                        />
                        <div className="text text-gray-400">
                          Scroll to see more
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Panel - Agent Display Section */}
              <div
                className="w-full md:flex-1  flex bg-transparent sm:items-center justify-center p-4 md:p-8 min-h-[600px] sm:min-w-[450px]"
                style={{ height: "calc(100vh - 64px)" }}
              >
                <div
  className="p-2 flex justify-center rounded-[36px]"
  style={{
    background:
      "linear-gradient(to bottom, #FFFFFF 0%, #FFFFFF 39%)",
    boxShadow: "0px 8px 34px 0px rgba(0, 0, 0, 0.08)",
    backdropFilter: "blur(32px)"
  }}
>
                  
                  <div
  className="w-full max-w-sm md:max-w-md md:w-[350px] sm:pb-8 md:h-[600px] min-h-[500px] rounded-[2rem] md:rounded-[2.5rem] flex items-center justify-center overflow-hidden relative"
  style={{
  background: "linear-gradient(to bottom, #D61920 0%, #D61920 20%, #FFFFFF 35%, #FFFFFF 100%)"
}}

>

                    

                    <div className="mt-24 flex-1 flex flex-col items-center justify-between relative z-10 px-4 py-6 h-full">
                      <img
                        src={hoablGoldLogo}
                        alt="Logo"
                        className="h-10 md:h-24 object-contain"
                      />

                      <div className="flex-1 flex flex-col items-center justify-center">
                        <div className="relative mb-4 md:mb-8 flex justify-center items-center">
                          <div className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 xl:w-64 xl:h-64 relative">
                            {/* Outer circle with radial gradient, border and 3D shadow */}
                            <div
                              className="absolute inset-0 rounded-full border-2 sm:border-3 border-white shadow-2xl"
                              style={{
                                background:
                                  "radial-gradient(circle, rgba(251, 113, 133, 0.6) 0%, rgba(251, 113, 133, 0.3) 20%, rgba(255, 255, 255, 0.8) 100%)",
                                boxShadow:
                                  "0 25px 50px rgba(227, 233, 248, 0.8), 0 15px 30px rgba(227, 233, 248, 0.6), 0 8px 20px rgba(227, 233, 248, 0.4), 0 4px 10px rgba(251, 113, 133, 0.3)"
                              }}
                            ></div>

                            {/* Middle circle for additional depth */}
                            <div
                              className="absolute inset-1 sm:inset-1.5 md:inset-2 lg:inset-2.5 xl:inset-3 rounded-full"
                              style={{
                                background:
                                  "radial-gradient(circle, rgba(251, 113, 133, 0.4) 0%, rgba(251, 113, 133, 0.2) 20%, rgba(255, 255, 255, 0.9) 100%)"
                              }}
                            ></div>

                            {/* Inner circle containing the image with 3D shadow */}
                            <div
                              className="absolute inset-6 sm:inset-7 md:inset-9 lg:inset-11 xl:inset-12 rounded-full overflow-hidden bg-white z-10 border-4 border-rose-200"
                              style={{
                                boxShadow:
                                  "0 20px 40px rgba(0, 0, 0, 0.1), 0 8px 16px rgba(251, 113, 133, 0.2)"
                              }}
                            >
                              <img
  src={AgentImage}
  alt={selectedConfig?.agent.agentName || "Agent"}
  className="w-full h-full object-cover object-top"
/>

                            </div>

                            {/* Subtle inner glow */}
                            <div
                              className="absolute inset-6 sm:inset-7 md:inset-9 lg:inset-11 xl:inset-12 rounded-full z-20 pointer-events-none"
                              style={{
                                boxShadow:
                                  "inset 0 0 10px rgba(251, 113, 133, 0.1)"
                              }}
                            ></div>
                          </div>
                        </div>

                        <div className="mt-8 flex flex-col items-center text-center">
                          <h1 className="font-inter text-lg md:text-2xl lg:text-3xl font-normal text-black">
                            Priya
                          </h1>

                          

                          
                        </div>
                      </div>

                      <div className="mt-4 md:mt-8 pb-16">
                        <Dialog
                          open={isDialogOpen}
                          onOpenChange={setIsDialogOpen}
                        >
                          <DialogTrigger asChild>
                            <button
                              className="font-manrope flex items-center text-white justify-center gap-2 px-4 md:px-6 lg:px-8 py-2 md:py-3 rounded-[40px] border border-[#EBEAEA] transition-all duration-300 hover:scale-105 hover:shadow-lg text-sm md:text-base"
                              style={{
                                background: "rgba(218, 28, 57, 1)"
                              }}
                            >
                              <Phone
                                size={14}
                                className="md:w-4 md:h-4 lg:w-5 lg:h-5"
                              />
                              <span>Talk to Us</span>
                            </button>
                          </DialogTrigger>
                          <DialogContent className="max-w-none h-screen w-screen p-0 border-none [&>button]:hidden">
                            {renderVoiceDashboard()}
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
